import { createContext, useCallback, useContext, useEffect, useState } from 'react'

const ToastCtx = createContext(null)
let _externalPush = null

const MAX_TOASTS = 4
const DEDUP_WINDOW_MS = 3000

export function pushToast(t) { _externalPush?.(t) }

export function ToastProvider({ children }) {
  const [items, setItems] = useState([])

  const push = useCallback((t) => {
    const id = crypto.randomUUID?.() || String(Math.random())
    const item = {
      id,
      kind:  t.kind || 'info',
      title: t.title,
      body:  t.body,
      ttl:   t.ttl ?? (t.kind === 'error' ? 6000 : 3500),
      key:   `${t.kind}:${t.title}:${t.body || ''}`,
      ts:    Date.now(),
    }
    setItems((xs) => {
      // Dedup: collapse to a count chip if same key shown recently.
      const recent = xs.find((x) => x.key === item.key && (item.ts - x.ts) < DEDUP_WINDOW_MS)
      if (recent) {
        return xs.map((x) => x === recent ? { ...x, count: (x.count || 1) + 1, ts: item.ts } : x)
      }
      const next = [...xs, item]
      // Cap to N most recent
      return next.length > MAX_TOASTS ? next.slice(-MAX_TOASTS) : next
    })
    if (item.ttl > 0) setTimeout(() => dismiss(id), item.ttl)
    return id
  }, [])

  const dismiss = useCallback((id) => setItems((xs) => xs.filter((x) => x.id !== id)), [])
  const dismissAll = useCallback(() => setItems([]), [])

  useEffect(() => { _externalPush = push; return () => { _externalPush = null } }, [push])

  return (
    <ToastCtx.Provider value={{ push, dismiss, dismissAll }}>
      {children}
      <div className="fixed bottom-24 md:bottom-6 right-4 md:right-6 z-50 flex flex-col gap-2 max-w-[calc(100vw-2rem)] md:max-w-sm pointer-events-none">
        {items.map((t) => (
          <div key={t.id}
               onClick={() => dismiss(t.id)}
               className={`pointer-events-auto px-4 py-3 rounded-themed border bg-card shadow-themed cursor-pointer animate-system-open ${
                 t.kind === 'error'   ? 'border-red-400/60'    :
                 t.kind === 'warn'    ? 'border-yellow-400/60' :
                 t.kind === 'success' ? 'border-[var(--accent)]' :
                                        'border-card'
               }`}>
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                {t.title && <div className="font-display text-sm truncate">{t.title}</div>}
                {t.body  && <div className="text-xs opacity-80 mt-0.5 break-words">{t.body}</div>}
              </div>
              {t.count > 1 && (
                <span className="text-[10px] opacity-70 num bg-white/10 px-1.5 py-0.5 rounded-full">
                  ×{t.count}
                </span>
              )}
            </div>
          </div>
        ))}
        {items.length >= 2 && (
          <button onClick={dismissAll}
                  className="pointer-events-auto self-end text-[10px] opacity-50 hover:opacity-100 px-2 py-1">
            Alle schließen
          </button>
        )}
      </div>
    </ToastCtx.Provider>
  )
}

export function useToast() {
  return useContext(ToastCtx) || { push: pushToast, dismiss: () => {}, dismissAll: () => {} }
}
